import { Component, Input, OnDestroy } from '@angular/core';
import { forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { ListRelationshipsForBulkEligibilityDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/list-relationships-for-bulk-eligibility-dto';
import {
    BulkUpdateRelationshipEligibilityRequest,
    BulkUpdateRelationshipEligibilityService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/bulk-update-relationship-eligibility';
import {
    ListRelationshipsForBulkActionRequest,
    ListRelationshipsForBulkEligibilityService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/list-relationships-for-bulk-eligibility';
import { AdminSuppliersBulkActionsService } from './bulk-actions.service';

@LifecycleAware()
@Component({
    selector: 'admin-suppliers-bulk-eligibility',
    template: `
        <bulk-action-layout
            [pageHeader]="(enable ? 'Enable' : 'Disable') + ' Early Payment Eligibility'"
            [supplierCount]="(relationshipKeys$ | async).length"
            [confirmButtonText]="(enable ? 'Enable' : 'Disable') + ' Eligibility'"
            [disableConfirm]="!checkableRowsService.checkedRows.length"
            (confirmClickHandler)="submitUpdate()"
        >
            <div supplier-table>
                <lsq-data-table [dataSource]="dataSource" [isPaginated]="true" [layoutFixed]="true">
                    <lsq-checkbox-column
                        [checkableRowsService]="checkableRowsService"
                        [column]="{
                            key: 'relationshipKey',
                            sortable: false,
                            width: '4rem'
                        }"
                    >
                    </lsq-checkbox-column>
                    <lsq-column
                        [column]="{
                            key: 'supplierName',
                            header: 'Supplier Name',
                            sortable: false,
                            truncateTextCss: true,
                            width: '20rem'
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            key: 'buyerName',
                            header: 'Buyer Relationship',
                            sortable: false,
                            truncateTextCss: true,
                            width: '20rem'
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            key: 'eligible',
                            header: 'Current Eligibility',
                            sortable: false,
                            width: '10rem'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            {{ row.eligible ? 'Enabled' : 'Disabled' }}
                        </ng-template>
                    </lsq-column>
                </lsq-data-table>
            </div>

            <div confirm-card>
                <p class="data-label">Update Eligibility</p>
                <p class="header" [style.marginBottom]="'2rem'">
                    {{ enable ? 'Enabled' : 'Disabled' }}
                </p>
                <p class="body" *ngIf="enable">
                    This action will toggle eligibility status to enabled, allowing all of the
                    selected suppliers the ability to receive early payments within FastTrack.
                </p>
                <p class="body" *ngIf="!enable">
                    This action will toggle eligibility status to disabled, which will remove the
                    ability for all of the selected suppliers to make early payments within
                    FastTrack.
                </p>
            </div>
        </bulk-action-layout>
    `
})
export class AdminSuppliersBulkEligibilityComponent implements OnDestroy {
    @Input() public enable: boolean;

    public relationshipKeys$ = this._adminSuppliersBulkActionsService.relationshipKeys$;

    public checkableRowsService = this._adminSuppliersBulkActionsService.checkableRowsService;

    public _skipSubject = new BehaviorSubject<number>(0);
    public _takeSubject = new BehaviorSubject<number>(10);
    public skip$ = this._skipSubject.asObservable();
    public take$ = this._takeSubject.asObservable();

    private _relationshipKeysToFetch$ = combineLatest([
        this.relationshipKeys$,
        this.skip$,
        this.take$
    ]).pipe(
        map(([relationshipKeys, skip, take]) =>
            !!relationshipKeys ? relationshipKeys.slice(skip, skip + take) : []
        )
    );

    // This is an unconventional data source, because we have not yet built out a backend that
    // keeps track of which rows we have checked, only the frontend knows. So we have to do manual
    // pagination by passing the relationshipKeys we want data for to the endpoint.
    public dataSource = new LsqListSource<ListRelationshipsForBulkEligibilityDto>({
        data$: this._listRelationshipsForBulkEligibility.dataStream,
        rows$: this._listRelationshipsForBulkEligibility.dataStream.pipe(pluck('rows')),
        error$: this._listRelationshipsForBulkEligibility.errorStream,
        loading$: this._listRelationshipsForBulkEligibility.isInFlightStream,
        totalCount$: this.relationshipKeys$.pipe(map((relationships) => relationships?.length)),
        skip: 0,
        take: 10,
        handleSkipChange: (skip) => {
            this.dataSource.skip = skip;
            this._skipSubject.next(skip);
        },
        handleTakeChange: (take) => {
            this.dataSource.take = take;
            this._takeSubject.next(take);
        }
    });

    public ngOnDestroy() {}

    constructor(
        private _adminSuppliersBulkActionsService: AdminSuppliersBulkActionsService,
        private _listRelationshipsForBulkEligibility: ListRelationshipsForBulkEligibilityService,
        private _bulkUpdateRelationshipEligibility: BulkUpdateRelationshipEligibilityService
    ) {
        this._relationshipKeysToFetch$.pipe(forLifeOf(this)).subscribe((relationshipKeys) => {
            this._listRelationshipsForBulkEligibility.execute(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys
                })
            );
            this.checkableRowsService.setCurrentPageKeys(relationshipKeys);
        });
    }

    public async submitUpdate(): Promise<void> {
        this._adminSuppliersBulkActionsService.setIsSubmitting(true);

        // Need to get the Supplier and Buyer names of all checked rows,
        // since we only currently have the ones for the current page of the table.
        const checkedRelationships = (
            await this._listRelationshipsForBulkEligibility.executeOnce(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys: this.checkableRowsService.checkedRows as string[]
                })
            )
        ).rows;

        try {
            await this._bulkUpdateRelationshipEligibility.executeOnce(
                new BulkUpdateRelationshipEligibilityRequest({
                    relationshipKeys: checkedRelationships.map((rel) => rel.relationshipKey),
                    eligibility: this.enable
                })
            );

            this._adminSuppliersBulkActionsService.showConfirmationModal(
                `Supplier Eligibility ${this.enable ? 'Enabled' : 'Disabled'}`,
                this.enable
                    ? 'The following suppliers have now enabled eligibility:'
                    : 'The following suppliers have now been disabled for eligibility',
                checkedRelationships
            );
        } catch (error) {
            console.log(error);
        }
        this._adminSuppliersBulkActionsService.setIsSubmitting(false);
    }
}
